import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { fetchStudentCourses } from "../../../redux/Actions/adminDeveloperStudentCoursesAction";
import AdminNavbar from "./AdminNavbar";
import api from "lib/api/api";

const StudentCourseManager = () => {
  const dispatch = useDispatch();
  const { loading, data, error } = useSelector((s) => s.studentCourses);

  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [filterText, setFilterText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  useEffect(() => {
    dispatch(fetchStudentCourses());
  }, [dispatch]);

  const handleArchive = async (id) => {
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/student-course/${id}/archive`);
      setSuccessMessage("Student course archived successfully!");
      dispatch(fetchStudentCourses());
    } catch (err) {
      let backendMsg =
        err.response?.data?.message || err.message || "Error archiving student course";
      if (typeof backendMsg !== "string") {
        backendMsg = JSON.stringify(backendMsg);
      }
      setErrorModalMessage(backendMsg);
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleUnarchive = async (id) => {
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/student-course/${id}/unarchive`);
      setSuccessMessage("Student course unarchived successfully!");
      dispatch(fetchStudentCourses());
    } catch (err) {
      let backendMsg =
        err.response?.data?.message || err.message || "Error unarchiving student course";
      if (typeof backendMsg !== "string") {
        backendMsg = JSON.stringify(backendMsg);
      }
      setErrorModalMessage(backendMsg);
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/student-course/archive-bulk`, selectedIds);
      setSuccessMessage(`${selectedIds.length} courses archived successfully!`);
      setSelectedIds([]);
      dispatch(fetchStudentCourses());
    } catch (err) {
      let backendMsg = err.response?.data?.message || err.message || "Error bulk archiving";
      setErrorModalMessage(typeof backendMsg === "string" ? backendMsg : JSON.stringify(backendMsg));
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleBulkUnarchive = async () => {
    if (selectedIds.length === 0) return;
    try {
      setIsLoadingAction(true);
      await api.put(`/admin-developer/student-course/unarchive-bulk`, selectedIds);
      setSuccessMessage(`${selectedIds.length} courses unarchived successfully!`);
      setSelectedIds([]);
      dispatch(fetchStudentCourses());
    } catch (err) {
      let backendMsg = err.response?.data?.message || err.message || "Error bulk unarchiving";
      setErrorModalMessage(typeof backendMsg === "string" ? backendMsg : JSON.stringify(backendMsg));
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setIsLoadingAction(true);
      const res = await api.delete(`/admin-developer/student-course/${id}`);
      let msg = res.data?.message || "Student course deleted successfully!";
      if (typeof msg !== "string") {
        msg = JSON.stringify(msg);
      }
      setSuccessMessage(msg);
      dispatch(fetchStudentCourses());
    } catch (err) {
      let backendMsg =
        err.response?.data?.message || err.message || "Error deleting student course";
      if (typeof backendMsg !== "string") {
        backendMsg = JSON.stringify(backendMsg);
      }
      setErrorModalMessage(backendMsg);
      setErrorModalOpen(true);
    } finally {
      setIsLoadingAction(false);
    }
  };

  useEffect(() => {
    if (successMessage) {
      const timeout = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timeout);
    }
  }, [successMessage]);

  const filteredData = data ? data.filter((sc) =>
    sc.courseTitle?.toLowerCase().includes(filterText.toLowerCase())
  ) : [];

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(filteredData.map((sc) => sc.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (event, id) => {
    if (event.target.checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };

  return (
    <div>
      <AdminNavbar />
      <Container>
        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom>
            Student Courses
          </Typography>

          <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <TextField
              label="Filter by Course Title"
              variant="outlined"
              size="small"
              sx={{ width: '300px' }}
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />

            {selectedIds.length > 0 && (
              <Box>
                <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                  {selectedIds.length} selected
                </Typography>
                <Button 
                  variant="contained" 
                  color="warning" 
                  onClick={handleBulkArchive}
                  disabled={isLoadingAction}
                  sx={{ mr: 1 }}
                >
                  Archive Selected
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={handleBulkUnarchive}
                  disabled={isLoadingAction}
                >
                  Unarchive Selected
                </Button>
              </Box>
            )}
          </Box>

          {loading && <CircularProgress />}
          {error && <Alert severity="error">{error}</Alert>}

          {isLoadingAction && <CircularProgress />}
          {successMessage && <Alert severity="success">{successMessage}</Alert>}

          {filteredData && filteredData.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedIds.length > 0 && selectedIds.length < filteredData.length}
                        checked={filteredData.length > 0 && selectedIds.length === filteredData.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Student Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Course Title</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Start Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>End Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Active</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData.map((sc) => {
                    const isSelected = selectedIds.includes(sc.id);
                    return (
                    <TableRow key={sc.id} selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => handleSelectOne(e, sc.id)}
                        />
                      </TableCell>
                      <TableCell>{`${sc.studentFirstName} ${sc.studentLastName}`}</TableCell>
                      <TableCell>{sc.courseTitle}</TableCell>
                      <TableCell>
                        {new Date(sc.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(sc.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{sc.statusName}</TableCell>
                      <TableCell>{sc.active ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {sc.active ? (
                          <Button
                            variant="contained"
                            color="warning"
                            onClick={() => handleArchive(sc.id)}
                            disabled={isLoadingAction}
                            sx={{ mr: 1 }}
                          >
                            Archive
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={() => handleUnarchive(sc.id)}
                            disabled={isLoadingAction}
                            sx={{ mr: 1 }}
                          >
                            Unarchive
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleDelete(sc.id)}
                          disabled={isLoadingAction}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            !loading && <Typography>No student courses available</Typography>
          )}
        </Box>
      </Container>

      <Dialog
        open={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        aria-labelledby="error-dialog-title"
      >
        <DialogTitle id="error-dialog-title">Action Failed</DialogTitle>
        <DialogContent>
          <DialogContentText>{errorModalMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorModalOpen(false)} autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StudentCourseManager;
